export * as ConfigCommand from "./command"

import * as Log from "@opencode-ai/core/util/log"
import { Schema } from "effect"
import { NamedError } from "@opencode-ai/core/util/error"
import { Glob } from "@opencode-ai/core/util/glob"
import { Bus } from "@/bus"
import { zod } from "@/util/effect-zod"
import { withStatics } from "@/util/schema"
import { configEntryNameFromPath } from "./entry-name"
import * as ConfigMarkdown from "./markdown"
import { ConfigModelID } from "./model-id"
// kilocode_change start
// import { KilocodeConfig } from "@/kilocode/config/config"
import type { Warning } from "./config"
// kilocode_change end

const log = Log.create({ service: "config" })

// infcode_change start
function formatIssues(issues: Array<{ path: PropertyKey[]; message: string }>) {
  return issues
    .map((issue) => {
      const loc = issue.path.map(String).join(".")
      if (!loc) return issue.message
      return `${loc}: ${issue.message}`
    })
    .join("\n")
}

async function handleInvalid(item: string, issues: Array<{ path: PropertyKey[]; message: string }>, cause: Error, warnings?: Warning[]) {
  const text = formatIssues(issues)
  const message = text ? `Config file at ${item} is invalid: ${text}` : `Config file at ${item} is invalid`
  if (warnings) warnings.push({ path: item, message, detail: text || undefined })
  try {
    const { Session } = await import("@/session/session")
    Bus.publish(Session.Event.Error, { error: new NamedError.Unknown({ message }).toObject() })
  } catch (err) {
    log.warn("could not publish session error", { message, err })
  }
  log.error("failed to load command", { command: item, err: cause, message })
}
// infcode_change end

export const Info = Schema.Struct({
  template: Schema.String,
  description: Schema.optional(Schema.String),
  agent: Schema.optional(Schema.String),
  model: Schema.optional(ConfigModelID),
  subtask: Schema.optional(Schema.Boolean),
}).pipe(withStatics((s) => ({ zod: zod(s) })))

export type Info = Schema.Schema.Type<typeof Info>

// kilocode_change start
export async function load(dir: string, warnings?: Warning[]) {
  // kilocode_change end
  const result: Record<string, Info> = {}
  for (const item of await Glob.scan("{command,commands}/**/*.md", {
    cwd: dir,
    absolute: true,
    dot: true,
    symlink: true,
  })) {
    const md = await ConfigMarkdown.parse(item).catch(async (err) => {
      const message = ConfigMarkdown.FrontmatterError.isInstance(err)
        ? err.data.message
        : `Failed to parse command ${item}`
      // kilocode_change start
      if (warnings) warnings.push({ path: item, message })
      try {
        const { Session } = await import("@/session/session")
        Bus.publish(Session.Event.Error, { error: new NamedError.Unknown({ message }).toObject() })
      } catch (e) {
        log.warn("could not publish session error", { message, err: e })
      }
      log.error("failed to load command", { command: item, err })
      return undefined
      // kilocode_change end
    })
    if (!md) continue

    // kilocode_change start
    // infcode_change start
    // const patterns = [
    //   "/.kilo/command/",
    //   "/.kilo/commands/",
    //   "/.kilocode/command/",
    //   "/.kilocode/commands/",
    //   "/.opencode/command/",
    //   "/.opencode/commands/",
    //   "/command/",
    //   "/commands/",
    // ]
    const patterns: string[] = []
    // infcode_change end
    // kilocode_change end
    const name = configEntryNameFromPath(item, patterns)

    const config = {
      name,
      ...md.data,
      template: md.content.trim(),
    }
    const parsed = Info.zod.safeParse(config)
    if (parsed.success) {
      result[config.name] = parsed.data
      continue
    }
    // kilocode_change start
    await handleInvalid(item, parsed.error.issues, parsed.error, warnings) // infcode_change
    // kilocode_change end
  }
  return result
}
